/* CaseSubmittedDialog — iter98.69 (extended in iter98.76)
 *
 * Premium confirmation modal shown for high-stakes case actions across
 * BOTH doctor and planner portals. Replaces the thin sonner toast that
 * used to confirm consequential actions in the app.
 *
 * Flows currently supported (per the iter98.76 universal upgrade):
 *   - "planning"                   — doctor submits to CoE planning
 *   - "production"                 — doctor submits to manufacturing
 *   - "plan_sent_to_doctor"        — planner sends plan for approval
 *   - "planner_query_to_doctor"    — planner pauses + queries doctor
 *   - "doctor_revision_to_planner" — doctor requests revisions
 *
 * Visual goal: celebratory but professional. Animated success ring +
 * a per-flow "what happens next" itinerary so the actor doesn't have
 * to wonder what comes next, plus two clear CTAs. Closing by overlay-
 * click or ESC triggers `onViewCase` (or `onPrimary`) by default —
 * never silently strands the user mid-flow.
 *
 * Tone variants (`tone` field on FLOW):
 *   - "teal"  → forward-motion / success (default)
 *   - "amber" → paused / hold-and-query
 *   - "blue"  → revision / advisory feedback
 */
import React from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
    CheckCircle, ArrowRight, Sparkle, Flask,
    UserGear, ChartLine, House,
    // iter98.76 — universal-dialog icons.
    HandPalm, ChatCircleDots, PaperPlaneTilt, PencilSimple,
    Stethoscope,
    // iter98.99g — scan-visit booked confirmation icons.
    Camera, CloudArrowUp, CalendarCheck,
    // iter98.99i — Tier 1 universal dialog roll-out icons.
    Truck, Package, Receipt, ShieldCheck, MapPin,
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";

/* Per-flow content. Add a new entry here if a new high-stakes case
 * action is introduced — the dialog body adapts automatically. */
const FLOW = {
    planning: {
        eyebrow: "Sent to planning",
        title: "Your case is with our planners",
        body: (
            <>
                Our <b>Center of Excellence Team</b> will draft the treatment plan and send it back to you for approval.
            </>
        ),
        steps: [
            { icon: UserGear, label: "Planner review starts", sub: "Typically within 24 business hours." },
            { icon: ChartLine, label: "Treatment plan drafted", sub: "Per-tooth movements, aligner count, IPR, attachments." },
            { icon: Sparkle, label: "Sent back for approval", sub: "You'll get a notification — review on the case page." },
        ],
        primaryLabel: "View case",
        tone: "teal",
        icon: CheckCircle,
    },
    production: {
        eyebrow: "Sent to manufacturing",
        title: "Your case is now in the lab",
        body: (
            <>
                Your plan has been received by our manufacturing team and is queued for production.
            </>
        ),
        steps: [
            { icon: Flask, label: "Production starts", sub: "Lab technicians fabricate per your plan." },
            { icon: ChartLine, label: "Quality control", sub: "Independent QC sign-off before dispatch." },
            { icon: Sparkle, label: "Shipped to your clinic", sub: "Tracking + delivery ETA appear on the case page." },
        ],
        primaryLabel: "View case",
        tone: "teal",
        icon: CheckCircle,
    },
    /* iter98.81 — Doctor self-plan submission to the lab. Same downstream
     * pipeline as `production` BUT customised to celebrate the doctor's
     * authoring authority: trust badge in the hero + "as-is, no review"
     * wording in step 1 + slightly more reassuring sub-copy. Used by the
     * CaseSubmissionPage when planningMode === "doctor". */
    doctor_self_plan_to_production: {
        eyebrow: "Plan authored by you · Sent to lab",
        title: "Your plan is on its way to the lab",
        body: (
            <>
                <b>Plan authored by you</b> — production starts as-is. No additional review steps, no waiting on our planners.
            </>
        ),
        steps: [
            { icon: Flask, label: "Lab kicks off your plan as-is", sub: "Your authored prescription is the source of truth." },
            { icon: ChartLine, label: "Quality control", sub: "Independent QC sign-off on the appliances themselves before dispatch." },
            { icon: Sparkle, label: "Shipped to your clinic", sub: "Tracking + delivery ETA appear on the case page." },
        ],
        primaryLabel: "View case",
        tone: "teal",
        icon: CheckCircle,
        /* iter98.81 — small trust badge surfaced in the hero so the
         * doctor feels the authoring authority was respected. */
        trustBadge: "Plan authored by you — no further review steps",
    },
    /* iter98.76 — Planner → Doctor: plan ready for approval. */
    plan_sent_to_doctor: {
        eyebrow: "Sent for approval",
        title: "Plan is on its way to the doctor",
        body: (
            <>
                The doctor has been notified by WhatsApp &amp; email. They&apos;ll review the plan and either approve or request revisions.
            </>
        ),
        steps: [
            { icon: Stethoscope, label: "Doctor reviews the plan", sub: "Per-tooth movements, IPR, attachments, schedule." },
            { icon: PencilSimple, label: "Approval or revision feedback", sub: "If they request revisions, the case returns to your queue." },
            { icon: Flask, label: "Production starts on approval", sub: "Lab kicks off the moment the doctor signs off." },
        ],
        primaryLabel: "View case",
        tone: "teal",
        icon: PaperPlaneTilt,
    },
    /* iter98.76 — Planner → Doctor: pause + query. */
    planner_query_to_doctor: {
        eyebrow: "Query sent · case paused",
        title: "Doctor has your question",
        body: (
            <>
                The case is paused and the doctor sees your query in their <b>Awaiting your input</b> bucket. Authoring resumes the moment they respond.
            </>
        ),
        steps: [
            { icon: Stethoscope, label: "Doctor reviews your query", sub: "They get a notification with the context inline." },
            { icon: ChatCircleDots, label: "Doctor responds with details", sub: "Could be a re-scan, an extra photo, or a clarification." },
            { icon: UserGear, label: "Plan authoring resumes", sub: "The case auto-returns to your queue when they reply." },
        ],
        primaryLabel: "Back to queue",
        tone: "amber",
        icon: HandPalm,
    },
    /* iter98.76 — Doctor → Planner: revision request. */
    doctor_revision_to_planner: {
        eyebrow: "Revision request sent",
        title: "Your feedback is with the planning team",
        body: (
            <>
                Our planning team has been notified with your line-by-line feedback. They&apos;ll address it and send the updated plan back for your approval.
            </>
        ),
        steps: [
            { icon: UserGear, label: "Planner reviews your feedback", sub: "Each change is itemised — they see exactly what to adjust." },
            { icon: PencilSimple, label: "Plan updated per your notes", sub: "Per-tooth movements, IPR, aligner count — re-authored." },
            { icon: Sparkle, label: "Sent back for approval", sub: "You'll be notified when the revised plan is ready." },
        ],
        primaryLabel: "View case",
        tone: "blue",
        icon: PencilSimple,
    },
    /* iter98.77 — Doctor responds to a planner's "Hold & query", which
     * auto-resumes the case. Symmetric reward to the planner-side
     * planner_query_to_doctor flow so both portals share the same
     * "your action unblocked the loop" celebration. */
    doctor_resume_after_query: {
        eyebrow: "Case resumed",
        title: "Planner has your reply",
        body: (
            <>
                The case is back in the planning queue. Our team will pick it up the moment they&apos;re free — typically within a few business hours.
            </>
        ),
        steps: [
            { icon: UserGear, label: "Planner sees your reply inline", sub: "Your response is pinned at the top of their authoring view." },
            { icon: ChartLine, label: "Plan authoring resumes", sub: "They incorporate your input and finalise the per-tooth plan." },
            { icon: Sparkle, label: "Sent back for your approval", sub: "You'll get a notification once the updated plan is ready." },
        ],
        primaryLabel: "View case",
        tone: "emerald",
        icon: CheckCircle,
    },
    /* iter98.99g — IO Scans by Invisique · scan-visit booked.
     * Replaces the thin sonner success toast with a celebratory but
     * focused confirmation dialog so the doctor sees the EXACT next
     * sequence of events on their case (visit → upload → resume).
     * Tone is emerald to match the success/forward-motion feel of
     * the booking moment. Caller passes `visitMeta` so the title and
     * body show date + time + patient name. */
    scan_visit_booked: {
        eyebrow: "Scan visit booked",
        title: "We're set for your patient",
        // iter98.99l — Body paragraph removed per user direction. The
        // visit-meta chip + the "What happens next" itinerary together
        // already communicate everything the doctor needs to know, and
        // dropping the paragraph gives the itinerary room to breathe
        // without internal scrolling on laptop viewports.
        body: null,
        steps: [
            { icon: Camera,        label: "Our technician visits your clinic",      sub: "On-site capture: STL scans + 4-angle DSLR photo series." },
            { icon: CloudArrowUp,  label: "Records uploaded directly to your case", sub: "Same-day, direct to the case page — no emails or USB transfers." },
            { icon: Sparkle,       label: "Resume the case when scans arrive",      sub: "You'll get a nudge to pick planning mode, compliance & manufacturing speed." },
        ],
        primaryLabel: "Done",
        tone: "emerald",
        icon: CalendarCheck,
    },
    /* iter98.99i — Doctor approves the planner's treatment plan. The
     * case status flips to `assigned_to_production` (or production
     * pipeline equivalent) and billing kicks off. Symmetric pair to
     * `plan_sent_to_doctor` (planner-side). */
    doctor_approved_plan: {
        eyebrow: "Plan approved · case routed to production",
        title: "You've signed off — the lab takes it from here",
        body: (
            <>
                Your patient&apos;s plan is now <b>locked in</b>. Manufacturing kicks off as soon as the lab queue picks it up.
            </>
        ),
        steps: [
            { icon: Flask,       label: "Lab kicks off production",  sub: "Per your approved per-tooth plan — no further authoring needed." },
            { icon: ShieldCheck, label: "Independent QC sign-off",   sub: "Every appliance batch is QC'd before it leaves the lab." },
            { icon: Truck,       label: "Shipped to your clinic",    sub: "Tracking + ETA appear on the case page the moment it dispatches." },
        ],
        primaryLabel: "View case",
        tone: "emerald",
        icon: CheckCircle,
    },
    /* iter98.99i — QA approves the plan → sent to doctor for final
     * approval (Phase 2 billing flow). Used by both QaCaseDetail
     * (single-case page) and QaInbox (Self-Plan gating section). */
    qa_sent_to_doctor: {
        eyebrow: "QA cleared · doctor approval",
        title: "Plan is with the doctor for final sign-off",
        body: (
            <>
                QA-cleared and routed back. The doctor has been notified — they&apos;ll approve or request revisions.
            </>
        ),
        steps: [
            { icon: Stethoscope,    label: "Doctor reviews the plan",            sub: "Per-tooth movements, IPR, attachments, schedule." },
            { icon: PencilSimple,   label: "Approval or revision feedback",      sub: "If they request changes, the case returns to the planner with itemised feedback." },
            { icon: Flask,          label: "Production starts on approval",      sub: "Lab kicks off the moment the doctor signs off." },
        ],
        primaryLabel: "Back to QA inbox",
        tone: "teal",
        icon: PaperPlaneTilt,
    },
    /* iter98.99i — Production marks a case as shipped. The doctor +
     * patient both get tracking via WhatsApp + email. */
    case_shipped: {
        eyebrow: "Shipped · on its way to the clinic",
        title: "Your patient's smile is on the move",
        body: (
            <>
                Aligners have left the lab. The doctor and patient both get tracking via <b>WhatsApp + email</b>.
            </>
        ),
        steps: [
            { icon: Truck,       label: "In transit to the clinic", sub: "Carrier + tracking number are pinned to the case page." },
            { icon: MapPin,      label: "Live tracking updates",    sub: "The case page reflects shipment progress until delivered." },
            { icon: CheckCircle, label: "Delivered & ready to fit", sub: "Doctor activates treatment once aligners are received and tried in." },
        ],
        primaryLabel: "Back to queue",
        tone: "emerald",
        icon: Package,
    },
    /* iter98.99i — Technician completes the on-site scan visit. The
     * doctor is notified and can resume the case from where they left
     * off. Symmetric pair to `scan_visit_booked` (doctor-side).  */
    scan_visit_completed: {
        eyebrow: "Visit completed · records uploaded",
        title: "Records are in. Doctor takes it from here.",
        body: (
            <>
                The doctor has been notified — they can resume the case and finalise the treatment plan.
            </>
        ),
        steps: [
            { icon: CloudArrowUp, label: "Scans + photos pinned to the case", sub: "Direct to the case page — no email or USB transfers needed." },
            { icon: Stethoscope,  label: "Doctor reviews the records",        sub: "Clinical exam, diagnosis, and planning pick up from your handoff." },
            { icon: Sparkle,      label: "Doctor resumes the case",           sub: "Planning mode, compliance + manufacturing speed get picked next." },
        ],
        primaryLabel: "Back to visits",
        tone: "emerald",
        icon: CheckCircle,
    },
};


/* Tone → tailwind class lookups. Defined as a static map so Tailwind's
 * JIT picks them up during build (no dynamic class string interpolation). */
const TONE = {
    teal: {
        heroBg: "bg-gradient-to-br from-primary/12 via-primary/5 to-transparent",
        eyebrowBg: "bg-primary/10 text-primary",
        iconBg: "bg-primary text-white shadow-[0_8px_28px_rgba(20,184,166,0.35)]",
        ring: "border-primary/40",
        ctaBg: "bg-primary hover:bg-primary/90",
        stepIconBg: "bg-primary/10 text-primary",
        sparkleColor: "bg-primary",
    },
    amber: {
        heroBg: "bg-gradient-to-br from-amber-100/70 via-amber-50/40 to-transparent",
        eyebrowBg: "bg-amber-100 text-amber-800",
        iconBg: "bg-amber-500 text-white shadow-[0_8px_28px_rgba(245,158,11,0.35)]",
        ring: "border-amber-400/40",
        ctaBg: "bg-amber-600 hover:bg-amber-700",
        stepIconBg: "bg-amber-100 text-amber-700",
        sparkleColor: "bg-amber-400",
    },
    blue: {
        heroBg: "bg-gradient-to-br from-blue-100/70 via-blue-50/40 to-transparent",
        eyebrowBg: "bg-blue-100 text-blue-800",
        iconBg: "bg-blue-600 text-white shadow-[0_8px_28px_rgba(37,99,235,0.35)]",
        ring: "border-blue-500/40",
        ctaBg: "bg-blue-600 hover:bg-blue-700",
        stepIconBg: "bg-blue-100 text-blue-700",
        sparkleColor: "bg-blue-400",
    },
    /* iter98.77 — emerald for "case unblocked / loop closed" flows. */
    emerald: {
        heroBg: "bg-gradient-to-br from-emerald-100/70 via-emerald-50/40 to-transparent",
        eyebrowBg: "bg-emerald-100 text-emerald-800",
        iconBg: "bg-emerald-500 text-white shadow-[0_8px_28px_rgba(16,185,129,0.35)]",
        ring: "border-emerald-500/40",
        ctaBg: "bg-emerald-600 hover:bg-emerald-700",
        stepIconBg: "bg-emerald-100 text-emerald-700",
        sparkleColor: "bg-emerald-400",
    },
};


export default function CaseSubmittedDialog({
    open,
    flow = "planning",
    caseCode = "",
    onViewCase,
    onBackToDashboard,
    /* iter98.76 — universal: callers may relabel the CTAs. The defaults
     * come from the FLOW entry; these overrides win when provided. */
    primaryLabel,
    secondaryLabel,
    onPrimary,            // alias for onViewCase when used outside the
                          // case-submitted semantics (e.g. planner → queue)
    onSecondary,          // alias for onBackToDashboard
    /* iter98.99g — scan_visit_booked metadata. When the caller is the
     * IO-Scans booking flow, this object carries the date/time/patient
     * the doctor just scheduled so the hero can echo it back inline.
     * Shape: { date: "YYYY-MM-DD", time: "09:00", time_label,
     *          patient_name, fee }.  Other flows ignore this prop. */
    visitMeta = null,
}) {
    const content = FLOW[flow] || FLOW.planning;
    const tone = TONE[content.tone] || TONE.teal;
    const IconCmp = content.icon || CheckCircle;
    const primary = onPrimary || onViewCase;
    const secondary = onSecondary || onBackToDashboard;
    const primaryText = primaryLabel || content.primaryLabel;
    const secondaryText = secondaryLabel || "Back to dashboard";

    return (
        <Dialog
            open={open}
            // The modal must always end in a navigation — clicking the
            // overlay or hitting ESC routes to the primary action so
            // the user isn't stuck on the page mid-flow.
            onOpenChange={(o) => { if (!o) primary?.(); }}
        >
            <DialogContent
                /* iter98.99l — No internal scrolling. The dialog sizes
                   to its content; the underlying DialogContent primitive
                   provides a soft 88vh cap with a hidden scrollbar as
                   safety net for extreme viewports. */
                className="max-w-md p-0 gap-0 border-0 shadow-2xl overflow-hidden"
                data-testid="case-submitted-dialog"
                data-flow={flow}
            >
                {/* Hero — tone-tinted gradient + animated success ring */}
                <div className={`relative px-6 pt-6 pb-4 overflow-hidden ${tone.heroBg}`}>
                    {/* Decorative sparkle scatter */}
                    <Decorations sparkleClass={tone.sparkleColor} />

                    <AnimatePresence>
                        {open && (
                            <motion.div
                                initial={{ scale: 0, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                transition={{
                                    type: "spring",
                                    stiffness: 240,
                                    damping: 16,
                                    delay: 0.06,
                                }}
                                className={`relative mx-auto w-16 h-16 rounded-full grid place-items-center ${tone.iconBg}`}
                                data-testid="case-submitted-icon"
                            >
                                {/* Rotating outer ring */}
                                <motion.span
                                    aria-hidden
                                    className={`absolute inset-[-5px] rounded-full border-2 ${tone.ring}`}
                                    initial={{ rotate: 0, opacity: 0 }}
                                    animate={{ rotate: 360, opacity: 1 }}
                                    transition={{
                                        rotate: { duration: 14, repeat: Infinity, ease: "linear" },
                                        opacity: { duration: 0.4 },
                                    }}
                                />
                                {/* Inner icon (per-flow) */}
                                <motion.span
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ delay: 0.22, type: "spring", stiffness: 320 }}
                                >
                                    <IconCmp size={28} weight="fill" />
                                </motion.span>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    <div className="text-center mt-3.5">
                        <div
                            className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] ${tone.eyebrowBg}`}
                            data-testid="case-submitted-eyebrow"
                        >
                            <Sparkle size={10} weight="fill" /> {content.eyebrow}
                        </div>
                        <h2
                            className="font-display text-xl font-bold tracking-tight mt-2 leading-tight"
                            data-testid="case-submitted-title"
                        >
                            {content.title}
                        </h2>
                        {/* iter98.99l — For the scan_visit_booked flow the
                            case code is nested inside the visit-meta chip
                            (further down) so we suppress the floating pill
                            here to avoid the duplicate + out-of-place feel.
                            All other flows keep the standalone pill. */}
                        {caseCode && flow !== "scan_visit_booked" && (
                            <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-white border border-border font-mono text-[11.5px] font-semibold text-foreground shadow-sm">
                                {caseCode}
                            </div>
                        )}
                        {/* iter98.81 — Optional trust badge for flows that
                            want to surface a small reassurance phrase right
                            below the case code (e.g. "Plan authored by you
                            — no further review steps" on the self-plan
                            submission flow). Same tone as the eyebrow. */}
                        {content.trustBadge && (
                            <div
                                className={`mt-2 inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10.5px] font-semibold ${tone.eyebrowBg}`}
                                data-testid="case-submitted-trust-badge"
                            >
                                <Sparkle size={10} weight="fill" />
                                {content.trustBadge}
                            </div>
                        )}
                        {content.body && (
                            <p className="text-[12.5px] text-muted-foreground mt-2.5 leading-relaxed max-w-sm mx-auto">
                                {content.body}
                            </p>
                        )}
                        {/* iter98.99g/l — Scan-visit meta chip. Echoes the
                            booking payload (date · time · patient) PLUS the
                            new case code, all in one composed card so the
                            hero never feels like it has loose floating
                            elements. */}
                        {flow === "scan_visit_booked" && visitMeta && (
                            <div
                                className="mt-3 inline-flex flex-col px-4 py-2.5 rounded-xl bg-white border border-emerald-200 shadow-sm min-w-[260px]"
                                data-testid="case-submitted-visit-meta"
                            >
                                <div className="flex items-center gap-1.5 justify-center text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-700">
                                    <CalendarCheck size={11} weight="fill" />
                                    Visit Confirmed
                                </div>
                                <div className="text-[14px] font-semibold text-foreground leading-tight mt-1.5 text-center">
                                    {fmtVisitDate(visitMeta.date)}
                                </div>
                                <div className="text-[11.5px] text-muted-foreground leading-tight mt-0.5 text-center">
                                    {visitMeta.time_label || visitMeta.time}
                                    {visitMeta.patient_name ? ` · ${visitMeta.patient_name}` : ""}
                                </div>
                                {caseCode && (
                                    <div className="mt-2 pt-2 border-t border-emerald-100 flex items-center justify-center gap-1.5">
                                        <span className="text-[9.5px] uppercase tracking-[0.14em] font-bold text-muted-foreground/80">
                                            Case
                                        </span>
                                        <span className="font-mono text-[11px] font-semibold text-foreground">
                                            {caseCode}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* "What happens next" itinerary — no internal scroll;
                    grows naturally with content. */}
                <div className="px-6 py-4 bg-card">
                    <div className="text-[10.5px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-3">
                        What happens next
                    </div>
                    <ol className="space-y-3.5 relative">
                        {content.steps.map((s, idx) => {
                            const Icon = s.icon;
                            return (
                                <li
                                    key={idx}
                                    className="flex items-start gap-3"
                                    data-testid={`case-submitted-step-${idx}`}
                                >
                                    <motion.span
                                        initial={{ opacity: 0, x: -8 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.3 + idx * 0.08 }}
                                        className={`shrink-0 w-8 h-8 rounded-full grid place-items-center ${tone.stepIconBg}`}
                                    >
                                        <Icon size={15} weight="duotone" />
                                    </motion.span>
                                    <motion.div
                                        initial={{ opacity: 0, x: -4 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 0.35 + idx * 0.08 }}
                                        className="flex-1 min-w-0 pt-0.5"
                                    >
                                        <div className="text-[13.5px] font-semibold text-foreground leading-tight">
                                            {s.label}
                                        </div>
                                        <div className="text-[11.5px] text-muted-foreground mt-0.5 leading-snug">
                                            {s.sub}
                                        </div>
                                    </motion.div>
                                </li>
                            );
                        })}
                    </ol>
                </div>

                {/* CTAs — secondary is optional. When `onSecondary`/`onBackToDashboard`
                    is absent we render only the primary CTA so the dialog
                    stays focused. */}
                <div className="px-6 pb-4 pt-2 bg-card flex items-center gap-2 flex-wrap border-t border-border/50">
                    {secondary && (
                        <Button
                            onClick={secondary}
                            variant="outline"
                            size="sm"
                            className="rounded-full gap-1.5 h-9"
                            data-testid="case-submitted-dashboard-btn"
                        >
                            <House size={12} weight="bold" /> {secondaryText}
                        </Button>
                    )}
                    <Button
                        onClick={primary}
                        size="sm"
                        className={`rounded-full h-9 text-white gap-1.5 ${secondary ? "ml-auto" : "w-full justify-center"} ${tone.ctaBg}`}
                        data-testid="case-submitted-view-btn"
                    >
                        {primaryText} <ArrowRight size={12} weight="bold" />
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}

/* iter98.99g — Pretty-print "YYYY-MM-DD" → "Mon · 24 Feb 2026" for the
 * scan-visit confirmation chip. Centralised here (rather than computed
 * upstream in RequestScanVisitModal) so the format stays consistent if
 * other flows reuse the chip. */
function fmtVisitDate(iso) {
    if (!iso) return "";
    try {
        const d = new Date(`${iso}T00:00:00`);
        return d.toLocaleDateString("en-IN", {
            weekday: "short",
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return iso;
    }
}

/* Decorative sparkle scatter behind the success icon — adds the
 * "premium reward" feel without crossing into kitsch. Pure CSS, runs
 * only when the dialog is mounted. iter98.76 — accepts `sparkleClass`
 * so per-tone variants stay on-brand. */
function Decorations({ sparkleClass = "bg-primary" }) {
    return (
        <>
            <span className={`absolute top-3 left-4 w-1.5 h-1.5 rounded-full opacity-30 animate-ping [animation-duration:2200ms] [animation-delay:200ms] ${sparkleClass}`} />
            <span className={`absolute top-7 right-7 w-1 h-1 rounded-full opacity-40 animate-ping [animation-duration:2400ms] [animation-delay:500ms] ${sparkleClass}`} />
            <span className={`absolute bottom-4 left-10 w-1 h-1 rounded-full opacity-40 animate-ping [animation-duration:2600ms] [animation-delay:800ms] ${sparkleClass}`} />
            <span className={`absolute bottom-7 right-10 w-1.5 h-1.5 rounded-full opacity-25 animate-ping [animation-duration:2000ms] [animation-delay:0ms] ${sparkleClass}`} />
        </>
    );
}
